import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ChevronsUpDown } from "lucide-react";
import { fromJS } from "immutable";
import InputFieldDebounce from "../../components/ui/InputFieldDebounce";
import { SelectField } from "../../components/ui";
import LabelsSelect from "../../components/LabelsSelect";
import DatePicker from "../../board/components/TaskDetail/DatePicker";
import CustomField from "../CustomField";
import EmailInputFieldDebounce from "../../components/ui/EmailInputFieldDebounce";
import PhoneInputFieldDebounce from "../../components/ui/PhoneInputFieldDebounce";
import AddressGroupSelect from "../../components/AddressGroupSelect";
import ItemSelect from "../../components/ItemSelect";
import BucketSelect from "../../components/BucketSelect";
import AssigneesSelect from "../../components/AssigneesSelect";

const ContactForm = ({
  projectId,
  visibleFieldIds,
  isEditMode: _isEditMode,
  taskData,
  onDataChange,
  validationErrors,
}) => {
  const { t } = useTranslation();

  const fields =
    useSelector((state: any) => state.projectReducer.getIn(["project", "fields"])) || fromJS([]);
  const salutations = useSelector((state: any) => state.ruleReducer.getIn(["salutation"])).toJS();

  const customFields = fields.filter(
    (field: any) => field.get("isCustomField") && field.get("formType") == "CONTACT"
  );

  const getTaskFieldValue = (fieldId: string) => {
    return taskData.getIn([fieldId, "value"]);
  };

  const handleChangeField = (fieldId, fieldValue) => {
    onDataChange(fieldId, fieldValue);
  };

  const BUILD_FIELDS: Record<string, any> = {
    name: (
      <div>
        <InputFieldDebounce
          required
          testId="contact-form-name-input"
          name="name"
          label={t("Contact Name")}
          placeholder={t("Set contact name...")}
          value={getTaskFieldValue("name")}
          onChange={(value) => handleChangeField("name", value)}
          errorMessage={validationErrors.name}
        />
      </div>
    ),
    description: (
      <div>
        <InputFieldDebounce
          testId="contact-form-description-input"
          mode="textarea"
          label={t("Description")}
          placeholder={t("Set description...")}
          value={getTaskFieldValue("description")}
          onChange={(value) => handleChangeField("description", value)}
          errorMessage={validationErrors.description}
        />
      </div>
    ),
    email: (
      <div>
        <EmailInputFieldDebounce
          required
          testId="contact-form-email-input"
          name="email"
          label={t("Email")}
          placeholder={t("Set email...")}
          value={getTaskFieldValue("email")}
          onChange={(value) => handleChangeField("email", value)}
          errorMessage={validationErrors.email}
        />
      </div>
    ),
    phone: (
      <div>
        <PhoneInputFieldDebounce
          testId="contact-form-phone-input"
          label={t("Phone number")}
          placeholder={t("Set phone number...")}
          value={getTaskFieldValue("phone")}
          onChange={(value) => handleChangeField("phone", value)}
          errorMessage={validationErrors.phone}
        />
      </div>
    ),
    jobTitle: (
      <div>
        <InputFieldDebounce
          testId="contact-form-job-title-input"
          label={t("Job Title")}
          placeholder={t("Set job title...")}
          value={getTaskFieldValue("jobTitle")}
          onChange={(value) => handleChangeField("jobTitle", value)}
          errorMessage={validationErrors.jobTitle}
        />
      </div>
    ),
    salutation: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Salutation")}</label>
        <SelectField
          testId="contact-form-salutation-input"
          menuPlacement="auto"
          options={salutations}
          value={salutations.find((item) => item.value === getTaskFieldValue("salutation"))}
          onChange={(selectedOption) => {
            handleChangeField("salutation", selectedOption?.value);
          }}
          placeholder={t("Select...")}
          isClearable
          dropdownIndicator={<ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />}
        />
      </div>
    ),
    birthday: (
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Birthday")}</label>
        <DatePicker
          selected={getTaskFieldValue("birthday")}
          onChange={(val: string) => handleChangeField("birthday", val)}
          disabledSelectDate={{ after: new Date() }}
        />
      </div>
    ),
    account: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">
          {t("Primary account")}
        </label>
        <ItemSelect
          testId="contact-form-account-input"
          projectId={projectId}
          contentType="COMPANY"
          type="FORM"
          value={getTaskFieldValue("account")?.toJS()}
          onChange={(account: any) => handleChangeField("account", account)}
          placeholder={t("Select account")}
          isClearable
        />
      </div>
    ),
    assignees: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Owners")}</label>
        <AssigneesSelect
          type="FORM"
          value={getTaskFieldValue("assignees")?.toJS() || []}
          onChange={(assigneeIds: string[]) => handleChangeField("assignees", assigneeIds)}
          placeholder={t("Select owners")}
        />
      </div>
    ),
    labels: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Labels")}</label>
        <LabelsSelect
          projectId={projectId}
          contentType="CONTACT"
          type="FORM"
          value={getTaskFieldValue("labels")?.toJS() || []}
          onChange={(labelIds: string[]) => handleChangeField("labels", labelIds)}
          placeholder={t("Select labels")}
          showRemove
        />
      </div>
    ),
    bucketId: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Group")}</label>
        <BucketSelect
          projectId={projectId}
          contentType="CONTACT"
          type="FORM"
          value={getTaskFieldValue("bucketId")}
          onChange={(bucketId: string) => handleChangeField("bucketId", bucketId)}
          placeholder={t("Select group")}
          isClearable
        />
        {validationErrors.bucketId && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.bucketId}</p>
        )}
      </div>
    ),
    // All address
    addressGroup: (
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Address")}</label>
        <AddressGroupSelect
          testIdPrefix="contact-form-address"
          type="FORM"
          street={getTaskFieldValue("street")}
          onStreetChange={(street) => handleChangeField("street", street)}
          city={getTaskFieldValue("city") ? getTaskFieldValue("city").toJS() : undefined}
          onCityChange={(city) => handleChangeField("city", city)}
          state={getTaskFieldValue("state") ? getTaskFieldValue("state").toJS() : undefined}
          onStateChange={(state) => handleChangeField("state", state)}
          country={getTaskFieldValue("country") ? getTaskFieldValue("country").toJS() : undefined}
          onCountryChange={(country) => handleChangeField("country", country)}
          placeholder={t("Set a value...")}
        />
      </div>
    ),
    linkedIn: (
      <div>
        <InputFieldDebounce
          testId="contact-form-linkedin-input"
          label={t("LinkedIn")}
          placeholder={t("Set a link (e.g., linkedin.com/in/username)")}
          value={getTaskFieldValue("linkedIn")}
          onChange={(value) => handleChangeField("linkedIn", value)}
          errorMessage={validationErrors.linkedIn}
        />
      </div>
    ),
    instagram: (
      <div>
        <InputFieldDebounce
          testId="contact-form-instagram-input"
          label={t("Instagram")}
          placeholder={t("Set a link (e.g., instagram.com/username)")}
          value={getTaskFieldValue("instagram")}
          onChange={(value) => handleChangeField("instagram", value)}
          errorMessage={validationErrors.instagram}
        />
      </div>
    ),
    twitter: (
      <div>
        <InputFieldDebounce
          testId="contact-form-twitter-input"
          label={t("Twitter")}
          placeholder={t("Set a link (e.g., x.com/username)")}
          value={getTaskFieldValue("twitter")}
          onChange={(value) => handleChangeField("twitter", value)}
          errorMessage={validationErrors.twitter}
        />
      </div>
    ),
    facebook: (
      <div>
        <InputFieldDebounce
          testId="contact-form-facebook-input"
          label={t("Facebook")}
          placeholder={t("Set a link (e.g., facebook.com/username)")}
          value={getTaskFieldValue("facebook")}
          onChange={(value) => handleChangeField("facebook", value)}
          errorMessage={validationErrors.facebook}
        />
      </div>
    ),
  };

  return (
    <div className="space-y-4">
      {visibleFieldIds.map((visibleFieldId) => {
        const customField = customFields.find((field) => field.get("id") === visibleFieldId);
        if (customField && customField.get("id")) {
          const fieldData = {
            ...customField.toJS(),
            value: getTaskFieldValue(customField.get("id")),
          };
          return (
            <div className="w-full" key={`${customField.get("id")}`}>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                {t(customField.get("label"))}
              </label>
              <CustomField
                field={fieldData}
                onChange={(value: any) => {
                  handleChangeField(customField.get("id"), value);
                }}
              />
            </div>
          );
        }
        return <React.Fragment key={visibleFieldId}>{BUILD_FIELDS[visibleFieldId]}</React.Fragment>;
      })}
    </div>
  );
};

export default ContactForm;
